// ============================================================================
// CrewAhead — Job API
// Aligned with backend: JobPost model + serializers
// ============================================================================

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type { ApplicationStatus, MatchScoreBreakdownScore } from '../types/models';

// ── Types ────────────────────────────────────────────────────────────────────

// Nested taxonomy object returned by backend (keys camelCased by interceptor)
export interface TaxonomyRef {
  id: number;
}

export interface RankRef extends TaxonomyRef {
  rankId: string;
  rankName: string;
  level?: string | null;
}

export interface ShorePositionRef extends TaxonomyRef {
  positionId: string;
  positionName: string;
  level?: string | null;
}

export interface VesselTypeRef extends TaxonomyRef {
  vesselTypeName: string;
  category?: string | null;
}

// Nested certification object returned by backend (keys camelCased by interceptor)
export interface CertificationRef extends TaxonomyRef {
  certId: string;
  certName: string;
  stcwRegulation?: string | null;
}

export interface JobPost {
  id: number;
  title: string;
  description: string;
  responsibilities: string;
  requirements: string;
  benefits: string;
  companyName: string;
  postedByName: string;
  postedById: number;
  companyLogoUrl?: string | null;
  matchScore?: number;
  rank: RankRef | null;
  shorePosition: ShorePositionRef | null;
  vesselType: VesselTypeRef | null;
  positionOther: string | null;
  positionDisplay: string;
  requiredCertificates: CertificationRef[];
  contractType: string;
  contractDuration: string;
  rotationSchedule: string;
  salaryCurrency: string;
  salaryPeriod: string;
  salaryMinUsd: number | null;
  salaryMaxUsd: number | null;
  salaryDisplay: string;
  requiredExperienceMonths: number | null;
  requiredDocuments: string[];
  requiredEnglishLevel: string;
  requiredSkills: string[];
  ageLimit: number | null;
  joiningPort: string;
  joiningDate: string | null;
  tradingArea: string;
  vesselName: string;
  flagState: string;
  grt: number | null;
  isOnboard: boolean;
  status: 'draft' | 'published' | 'closed' | 'filled' | 'expired';
  isFeatured: boolean;
  isUrgent: boolean;
  applyByDate: string | null;
  maxApplications: number | null;
  viewsCount: number;
  applicationsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SmartJobPost {
  id: number;
  employer: number;
  companyName: string;
  title: string;
  status: 'draft' | 'open' | 'closed' | 'filled';
  targetRank: RankRef | null;
  vesselType: VesselTypeRef | null;
  requiredCertificates: CertificationRef[];
  requiredSkills: string[];
  minimumSeaTimeMonths: number;
  matrixComplianceRequired: boolean;
  createdAt: string;
  updatedAt: string;
  matchScore: number;
  isCompliant: boolean;
  complianceGaps: string[];
}

export interface JobPostInput {
  title?: string;
  description?: string;
  responsibilities?: string;
  requirements?: string;
  benefits?: string;
  job_type?: 'vessel' | 'shore';
  rank_id?: number | null;
  shore_position_id?: number | null;
  vessel_type_id?: number | null;
  required_certification_ids?: number[];
  contract_type?: string;
  rotation_schedule?: string;
  salary_currency?: string;
  salary_usd_min?: number | null;
  salary_usd_max?: number | null;
  min_experience_months?: number | null;
  max_experience_months?: number | null;
  min_age?: number | null;
  max_age?: number | null;
  english_level?: string;
  expires_at?: string | null;
  status?: 'draft' | 'published' | 'closed' | 'filled' | 'expired';
}

export interface JobApplication {
  id: number;
  job: number;
  jobDetail?: JobPost;
  applicantName: string;
  applicantEmail: string;
  applicantType: 'seafarer' | 'shore';
  applicant?: {
    id: number;
    email: string;
    fullName: string;
    currentRank?: number;
    currentRankName?: string;
    currentPosition?: number;
    currentPositionName?: string;
    skills?: string[];
  } | null;
  status: ApplicationStatus;
  coverLetter: string | null;
  expectedSalary: number | null;
  availabilityDate: string | null;
  appliedAt: string;
  statusUpdatedAt: string | null;
  matchScore?: number | null;
  matchBreakdown?: MatchScoreBreakdownScore | null;
}

export interface JobApplicationInput {
  cover_letter?: string;
  expected_salary?: number;
  availability_date?: string;
}

export interface SavedJob {
  id: number;
  jobPost: JobPost;
  savedAt: string;
}

// ── CRUD ─────────────────────────────────────────────────────────────────────

export async function getMyJobPosts(): Promise<JobPost[]> {
  const response = await apiClient.get<{ count: number; results: JobPost[] }>('/jobs/my-posts');
  return response.data.results;
}

export async function getPublishedJobs(params?: Record<string, string>): Promise<{ count: number; results: JobPost[] }> {
  const response = await apiClient.get('/jobs', { params });
  return response.data;
}

/**
 * GET /api/v1/jobs/onboard/ or /api/v1/jobs/shore/
 *
 * Public job browse endpoint with filtering and pagination.
 * Authentication is optional for the API; the dashboard page enforces login.
 */
export async function getPublicJobDetail(id: string | number): Promise<JobPost> {
  try {
    const response = await apiClient.get<JobPost>(`/jobs/public/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getPublicJobs(params?: {
  type?: 'onboard' | 'shore';
  rankId?: string;
  vesselTypeId?: string;
  shorePositionId?: string;
  category?: 'training';
  location?: string;
  page?: number;
  pageSize?: number;
}, signal?: AbortSignal): Promise<{ count: number; page: number; pageSize: number; totalPages: number; results: JobPost[] }> {
  try {
    const query = new URLSearchParams();
    const type = params?.type ?? 'onboard';

    if (params?.rankId) query.set('rank_id', params.rankId);
    if (params?.vesselTypeId) query.set('vessel_type_id', params.vesselTypeId);
    if (params?.shorePositionId) query.set('shore_position_id', params.shorePositionId);
    if (params?.category) query.set('category', params.category);
    if (params?.location) query.set('location', params.location);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const base = type === 'shore' ? API_ENDPOINTS.JOBS.SHORE : API_ENDPOINTS.JOBS.ONBOARD;
    const url = `${base}${query.toString() ? `?${query.toString()}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      page: number;
      pageSize: number;
      totalPages: number;
      results: JobPost[];
    }>(url, { signal });

    return {
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.pageSize,
      totalPages: response.data.totalPages,
      results: response.data.results as unknown as JobPost[],
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getSmartJobFeed(params?: Record<string, string>): Promise<{ count: number; results: SmartJobPost[] }> {
  const response = await apiClient.get<{ count: number; results: SmartJobPost[] }>('/jobs/smart-feed', { params });
  return response.data;
}

export async function getJobDetail(id: number): Promise<JobPost> {
  const response = await apiClient.get<JobPost>(`/jobs/${id}`);
  return response.data;
}

export async function createJobPost(data: JobPostInput): Promise<JobPost> {
  try {
    const response = await apiClient.post<JobPost>('/jobs', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function updateJobPost(id: number, data: Partial<JobPostInput>): Promise<JobPost> {
  try {
    const response = await apiClient.patch<JobPost>(`/jobs/${id}`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function deleteJobPost(id: number): Promise<void> {
  await apiClient.delete(`/jobs/${id}`);
}

// ── Applications ────────────────────────────────────────────────────────────

function normalizeJobApplication(raw: unknown): JobApplication {
  const r = raw as Record<string, unknown>;
  const job = (r.job as JobPost | undefined) ?? null;
  const applicant = (r.applicant as { email?: string; fullName?: string } | undefined) ?? null;

  return {
    id: r.id as number,
    job: job?.id ?? (r.jobId as number | undefined) ?? 0,
    jobDetail: job ?? undefined,
    applicantName: applicant?.fullName ?? '',
    applicantEmail: applicant?.email ?? '',
    applicantType: r.applicantType as JobApplication['applicantType'],
    status: r.status as JobApplication['status'],
    coverLetter: null,
    expectedSalary: null,
    availabilityDate: null,
    appliedAt: r.appliedAt as string,
    statusUpdatedAt: null,
    matchScore: typeof r.matchScore === 'number' ? r.matchScore : null,
  };
}

export async function applyForJob(jobId: number, data: JobApplicationInput = {}): Promise<JobApplication> {
  try {
    const response = await apiClient.post<JobApplication>('/applications', {
      jobId,
      ...data,
    });
    return normalizeJobApplication(response.data);
  } catch (error) {
    throw handleApiError(error);
  }
}

/** Get all applications to the employer's jobs (filtered client-side by job ID if needed) */
export async function getJobApplications(): Promise<JobApplication[]> {
  const response = await apiClient.get<{ count: number; results: JobApplication[] }>('/applications');
  return response.data.results.map(normalizeJobApplication);
}

export async function updateApplicationStatus(applicationId: number, status: ApplicationStatus, employerNotes?: string): Promise<JobApplication> {
  const response = await apiClient.patch<JobApplication>(`/applications/${applicationId}/status`, { status, employer_notes: employerNotes });
  return normalizeJobApplication(response.data);
}

// ── Saved Jobs ──────────────────────────────────────────────────────────────

export async function toggleSavedJob(jobId: number): Promise<{ saved: boolean }> {
  const response = await apiClient.post<{ saved: boolean }>(`/jobs/${jobId}/save`);
  return response.data;
}

export async function getSavedJobs(): Promise<{ count: number; results: JobPost[] }> {
  const response = await apiClient.get<{ count: number; results: JobPost[] }>('/jobs/saved');
  return response.data;
}

/** Close a published job */
export async function closeJobPost(id: number): Promise<JobPost> {
  return updateJobPost(id, { status: "closed" });
}

/** Reopen a closed job */
export async function reopenJobPost(id: number): Promise<JobPost> {
  return updateJobPost(id, { status: "published" });
}

// ── Job Alerts ───────────────────────────────────────────────────────────────

export interface JobAlert {
  id: number;
  name?: string;
  keywords: string;
  location: string;
  isActive: boolean;
  frequency: string;
  createdAt: string;
}

export async function getJobAlerts(): Promise<JobAlert[]> {
  const response = await apiClient.get<{ count: number; results: JobAlert[] }>('/jobs/alerts');
  return response.data.results;
}

export async function createJobAlert(data: Partial<JobAlert>): Promise<JobAlert> {
  const response = await apiClient.post<JobAlert>('/jobs/alerts', data);
  return response.data;
}

export async function updateJobAlert(id: number, data: Partial<JobAlert>): Promise<JobAlert> {
  const response = await apiClient.patch<JobAlert>(`/jobs/alerts/${id}`, data);
  return response.data;
}

export async function deleteJobAlert(id: number): Promise<void> {
  await apiClient.delete(`/jobs/alerts/${id}`);
}

// ── Barrel-compatible aliases (for useJobs.ts / barrel file) ───────────────
// These aliases match the naming convention expected by the barrel file.
// IDs accept string | number for compatibility with URL params.

const _toNum = (id: string | number): number => typeof id === 'string' ? Number(id) : id;

/** Alias: browse published jobs — merges filters + pagination into params */
export async function getJobs(
  filters?: Record<string, unknown>,
  pagination?: Record<string, unknown>
): Promise<{ count: number; results: JobPost[] }> {
  const params = { ...filters, ...pagination } as Record<string, string>;
  return getPublishedJobs(params);
}

/** Alias: getJobDetail accepting string IDs */
export async function getJob(id: string | number): Promise<JobPost> {
  return getJobDetail(_toNum(id));
}

/** Alias: applyForJob accepting string IDs */
export async function applyToJob(
  jobId: string | number,
  data?: JobApplicationInput
): Promise<JobApplication> {
  return applyForJob(_toNum(jobId), data ?? {});
}

/** Alias: getJobApplications — wraps in paginated shape */
export async function getApplications(
  _pagination?: Record<string, unknown>
): Promise<{ count: number; results: JobApplication[] }> {
  const apps = await getJobApplications();
  return { count: apps.length, results: apps };
}

/** Withdraw an application */
export async function withdrawApplication(applicationId: string | number): Promise<void> {
  await apiClient.delete(`/applications/${_toNum(applicationId)}`);
}

/** Save a job */
export async function saveJob(jobId: string | number): Promise<void> {
  await toggleSavedJob(_toNum(jobId));
}

/** Unsave a job */
export async function unsaveJob(jobId: string | number): Promise<void> {
  await toggleSavedJob(_toNum(jobId));
}

/** Check if a job is saved */
export async function isJobSaved(jobId: string | number): Promise<boolean> {
  const saved = await getSavedJobs();
  return saved.results.some((s) => s.id === _toNum(jobId));
}

/** Alias: toggleJobAlert — delegates to the core toggle function */
export async function toggleJobAlert(
  alertId: string | number,
  _isActive: boolean
): Promise<JobAlert> {
  return toggleJobAlertCore(_toNum(alertId));
}

// Rename the core function to avoid naming collision with the alias
async function toggleJobAlertCore(id: number): Promise<JobAlert> {
  const response = await apiClient.post<JobAlert>(`/jobs/alerts/${id}/toggle`);
  return response.data;
}

// ── Correct create payload type aligned to backend JobPost model ──────────

export interface JobPostCreatePayload {
  title: string;
  description: string;
  is_onboard: boolean;
  status: 'draft' | 'published' | 'closed' | 'filled';
  target_rank_id?: number | null;
  vessel_type_id?: number | null;
  vessel_name?: string;
  flag_state?: string;
  grt?: number | null;
  rotation_schedule?: string;
  joining_port?: string;
  joining_date?: string | null;
  trading_area?: string;
  required_certificate_ids?: number[];
  required_skills?: string[];
  age_limit?: number | null;
  shore_position_id?: number | null;
  contract_type?: string;
  contract_duration?: string;
  salary_min_usd?: number | null;
  salary_max_usd?: number | null;
  salary_currency?: string;
  salary_period?: string;
  required_experience_months?: number | null;
  required_english_level?: string;
  responsibilities?: string;
  requirements?: string;
  benefits?: string;
  reference_number?: string;
  apply_by_date?: string | null;
  is_urgent?: boolean;
  position_other?: string;
}

export async function createSeafarerJobPost(data: JobPostCreatePayload): Promise<JobPost> {
  try {
    const response = await apiClient.post<JobPost>('/jobs', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function createShoreJobPost(data: JobPostCreatePayload): Promise<JobPost> {
  try {
    const response = await apiClient.post<JobPost>('/jobs', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export interface JobPostUpdatePayload extends Partial<JobPostCreatePayload> {
  status?: 'draft' | 'published' | 'closed' | 'filled';
}

/** Full PATCH update for an existing job post */
export async function patchJobPost(id: number, data: JobPostUpdatePayload): Promise<JobPost> {
  try {
    const response = await apiClient.patch<JobPost>(`/jobs/${id}`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
